import { useState } from 'react';
import {
  Button,
  ConfigProvider,
  Drawer,
  Input,
  InputNumber,
  Popconfirm,
  Segmented,
  Switch,
} from 'antd';
import heIL from 'antd/locale/he_IL';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faPen, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { Money } from '../../components/Money';
import { Loader } from '../../components/Loader';
import { useBoxSettings, useUpdateBoxSettings } from '../../hooks/useBoxSettings';
import { useCashRegisters, useDeleteCashRegister, useSaveCashRegister } from '../../hooks/useCashFlow';
import { usePettyCashCategories, useSavePettyCashCategory } from '../../hooks/usePettyCash';
import { useLocationContext } from '../../auth/LocationContext';
import { useProjectContext } from '../../auth/ProjectContext';
import { CreditSettingsSection } from '../credit/CreditSettingsSection';
import type { CashMode, CashRegister, PettyCashCategory } from '@deposits/shared';

interface Props {
  open: boolean;
  onClose: () => void;
}

export function SettingsDrawer({ open, onClose }: Props) {
  const { selectedLocationId, selectedLocation } = useLocationContext();
  const { selectedProjectId, selectedProject } = useProjectContext();
  const { data: settings, isLoading } = useBoxSettings();
  const updateSettings = useUpdateBoxSettings();

  const setField = (
    patch: Partial<{
      handles_cash: boolean;
      handles_checks: boolean;
      cash_mode: CashMode;
      manage_locations: boolean;
      manage_projects: boolean;
      report_deposits_to_arbox: boolean;
      petty_cash_enabled: boolean;
      credit_enabled: boolean;
    }>,
  ) => {
    updateSettings.mutate(patch, {
      onError: (err) => toast.error(err.message || 'שגיאה בשמירת ההגדרות'),
    });
  };

  return (
    <Drawer
      title={<span className="text-heading-3-bold text-main-text">הגדרות</span>}
      placement="left"
      open={open}
      onClose={onClose}
      width={520}
      closable={false}
      styles={{ header: { borderBottom: 'none' } }}
    >
      <ConfigProvider locale={heIL} getPopupContainer={() => document.body}>
        {isLoading || !settings ? (
          <div className="flex justify-center py-12"><Loader /></div>
        ) : (
          <div className="flex flex-col gap-6">
            <section>
              <div className="font-semibold mb-3">אמצעי תשלום</div>
              <div className="flex flex-col gap-3">
                <ToggleRow
                  label="עבודה עם מזומן"
                  checked={settings.handles_cash}
                  onChange={(v) => setField({ handles_cash: v })}
                />
                <ToggleRow
                  label="עבודה עם צ'קים"
                  checked={settings.handles_checks}
                  onChange={(v) => setField({ handles_checks: v })}
                />
              </div>
            </section>

            <section>
              <div className="font-semibold mb-1">ניהול סניפים</div>
              <div className="text-small text-secondary-text mb-3">
                כשמכובה, כל הסניפים מנוהלים יחד כיחידה אחת (קופה וכספת משותפות), והתקבולים נמשכים מכלל
                הסניפים. מתאים לעסקים שמשתמשים בסניפים כהפרדה לוגית בלבד.
              </div>
              <ToggleRow
                label="ניהול נפרד לכל סניף"
                checked={settings.manage_locations}
                onChange={(v) => setField({ manage_locations: v })}
              />
            </section>

            <section>
              <div className="font-semibold mb-1">ניהול פרויקטים</div>
              <div className="text-small text-secondary-text mb-3">
                כשמופעל, כל פרויקט מנוהל בנפרד בתוך הסניף — קופות, כספת, הפקדות ומספור משלו — ובוחרים
                פרויקט בראש המסך. תקבולים ללא פרויקט מנוהלים יחד תחת „ללא פרויקט".
              </div>
              <ToggleRow
                label="ניהול נפרד לכל פרויקט"
                checked={settings.manage_projects}
                onChange={(v) => setField({ manage_projects: v })}
              />
            </section>

            <section>
              <div className="font-semibold mb-1">פקודות יומן</div>
              <div className="text-small text-secondary-text mb-3">
                כשמופעל, כל הפקדה חדשה (וביטול) נוספת אוטומטית כפקודת יומן בהנהלת החשבונות.
                אסמכתא 1 = מזהה סניף‑מספר הפקדה.
              </div>
              <ToggleRow
                label="הוסף הפקדות כפקודות יומן"
                checked={settings.report_deposits_to_arbox}
                onChange={(v) => setField({ report_deposits_to_arbox: v })}
              />
            </section>

            <section>
              <div className="font-semibold mb-1">קופה קטנה</div>
              <div className="text-small text-secondary-text mb-3">
                קופת מזומן להוצאות קטנות. טוענים אליה כסף מקבלות מזומן (הקבלות לא יופיעו יותר להפקדה או
                בסגירת קופה), ומתעדים כל הוצאה שיוצאת ממנה. קופה אחת לכל פרויקט.
              </div>
              <ToggleRow
                label="הפעל קופה קטנה"
                checked={settings.petty_cash_enabled}
                onChange={(v) => setField({ petty_cash_enabled: v })}
              />
              {settings.petty_cash_enabled && (
                <div className="mt-4">
                  <div className="text-small text-main-text mb-2">קטגוריות הוצאה</div>
                  <PettyCashCategoriesManager />
                </div>
              )}
            </section>

            <section>
              <div className="font-semibold mb-1">
                הפקדות אשראי
                {settings.credit_enabled && settings.manage_locations && selectedLocation
                  ? ` · ${selectedLocation.name}`
                  : ''}
              </div>
              <div className="text-small text-secondary-text mb-3">
                העברת תשלומי אשראי (כל תשלום בנפרד) מכרטסת החברה לכרטסת הבנק, עם מסמך שמתעד את ההעברה.
                ההגדרות נשמרות לכל סניף בנפרד.
              </div>
              <ToggleRow
                label="עבודה עם הפקדות אשראי"
                checked={settings.credit_enabled}
                onChange={(v) => setField({ credit_enabled: v })}
              />
              {settings.credit_enabled && (
                <div className="mt-4">
                  {selectedLocationId == null ? <Loader /> : <CreditSettingsSection locationId={selectedLocationId} />}
                </div>
              )}
            </section>

            {settings.handles_cash && (
              <section>
                <div className="font-semibold mb-1">אופן הפקדת המזומן</div>
                <div className="text-small text-secondary-text mb-3">
                  בסגירת קופה המזומן עובר דרך הקופה לכספת, ומשם מופקד לבנק. במצב ישיר המזומן מופקד
                  ישירות מהתקבולים (כמו היום).
                </div>
                <Segmented<CashMode>
                  block
                  value={settings.cash_mode}
                  onChange={(v) => setField({ cash_mode: v })}
                  options={[
                    { label: 'הפקדה ישירה', value: 'direct' },
                    { label: 'סגירת קופה', value: 'register' },
                  ]}
                />
              </section>
            )}

            {settings.handles_cash && settings.cash_mode === 'register' && (
              <section>
                <div className="font-semibold mb-1">
                  קופות{settings.manage_locations && selectedLocation ? ` · ${selectedLocation.name}` : ''}
                  {selectedProject ? ` · ${selectedProject.name}` : ''}
                </div>
                <div className="text-small text-secondary-text mb-3">
                  כל קופה מחזיקה עודף פתיחה שנשאר בקופה בכל סגירה.
                </div>
                {selectedLocationId == null ? (
                  <Loader />
                ) : (
                  <RegistersManager locationId={selectedLocationId} projectId={selectedProjectId} />
                )}
              </section>
            )}
          </div>
        )}
      </ConfigProvider>
    </Drawer>
  );
}

function ToggleRow({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-base">{label}</span>
      <Switch checked={checked} onChange={onChange} />
    </div>
  );
}

function RegistersManager({
  locationId,
  projectId,
}: {
  locationId: number;
  projectId: number | null;
}) {
  const { data: registers = [], isLoading } = useCashRegisters(locationId, projectId);
  const save = useSaveCashRegister();
  const del = useDeleteCashRegister();

  const [editing, setEditing] = useState<CashRegister | 'new' | null>(null);
  const [name, setName] = useState('');
  const [floatAmount, setFloatAmount] = useState<number>(0);

  const startNew = () => {
    setEditing('new');
    setName('');
    setFloatAmount(0);
  };
  const startEdit = (r: CashRegister) => {
    setEditing(r);
    setName(r.name);
    setFloatAmount(Number(r.float_amount));
  };

  const submit = () => {
    if (!name.trim()) {
      toast.error('יש להזין שם קופה');
      return;
    }
    const id = editing && editing !== 'new' ? editing.id : undefined;
    save.mutate(
      {
        id,
        body: {
          location_id: locationId,
          project_id: projectId ?? undefined,
          name: name.trim(),
          float_amount: floatAmount,
        },
      },
      {
        onSuccess: () => {
          toast.success('הקופה נשמרה');
          setEditing(null);
        },
        onError: (err) => toast.error(err.message || 'שגיאה בשמירת הקופה'),
      },
    );
  };

  if (isLoading) return <Loader />;

  return (
    <div className="flex flex-col gap-2">
      {registers.map((r) => (
        <div
          key={r.id}
          className="flex items-center justify-between border border-border rounded px-3 py-2 bg-white"
        >
          <div>
            <div className="font-medium">{r.name}</div>
            <div className="text-small text-secondary-text">
              עודף פתיחה: <Money amount={Number(r.float_amount)} />
            </div>
          </div>
          <div className="flex items-center gap-1">
            <IconBtn icon={faPen} onClick={() => startEdit(r)} />
            <Popconfirm
              title="למחוק את הקופה?"
              okText="מחק"
              cancelText="ביטול"
              onConfirm={() =>
                del.mutate(r.id, {
                  onSuccess: () => toast.success('הקופה נמחקה'),
                  onError: (err) => toast.error(err.message || 'שגיאה במחיקת הקופה'),
                })
              }
            >
              <IconBtn icon={faTrash} danger />
            </Popconfirm>
          </div>
        </div>
      ))}

      {editing ? (
        <div className="border border-primary rounded p-3 bg-blue-bg flex flex-col gap-3">
          <div>
            <div className="text-small text-main-text mb-1">שם הקופה</div>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="קופה ראשית" />
          </div>
          <div>
            <div className="text-small text-main-text mb-1">עודף פתיחה</div>
            <InputNumber
              value={floatAmount}
              onChange={(v) => setFloatAmount(Number(v ?? 0))}
              min={0}
              addonAfter="₪"
              style={{ width: '100%' }}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button onClick={() => setEditing(null)}>ביטול</Button>
            <Button type="primary" onClick={submit} loading={save.isPending}>
              שמור
            </Button>
          </div>
        </div>
      ) : (
        <Button icon={<FontAwesomeIcon icon={faPlus} />} onClick={startNew} className="mt-1">
          הוסף קופה
        </Button>
      )}
    </div>
  );
}

function IconBtn({
  icon,
  onClick,
  danger,
}: {
  icon: any;
  onClick?: () => void;
  danger?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className={`p-2 rounded hover:bg-grey-bg transition-colors ${
        danger ? 'text-error-red' : 'text-secondary-text hover:text-main-text'
      }`}
    >
      <FontAwesomeIcon icon={icon} style={{ fontSize: 13 }} />
    </button>
  );
}

/** Add, rename, hide/show expense categories. Never deleted — old expenses use them. */
function PettyCashCategoriesManager() {
  const { data: categories = [], isLoading } = usePettyCashCategories();
  const save = useSavePettyCashCategory();
  const [editing, setEditing] = useState<PettyCashCategory | 'new' | null>(null);
  const [name, setName] = useState('');

  const submit = () => {
    if (!name.trim()) {
      toast.error('יש להזין שם קטגוריה');
      return;
    }
    const id = editing && editing !== 'new' ? editing.id : undefined;
    save.mutate(
      { id, body: { name: name.trim() } },
      {
        onSuccess: () => setEditing(null),
        onError: (err) => toast.error(err.message || 'שגיאה בשמירת הקטגוריה'),
      },
    );
  };

  const toggle = (c: PettyCashCategory) =>
    save.mutate(
      { id: c.id, body: { name: c.name, is_active: !c.is_active } },
      { onError: (err) => toast.error(err.message || 'שגיאה בשמירת הקטגוריה') },
    );

  if (isLoading) return <Loader />;

  return (
    <div className="flex flex-col gap-2">
      {categories.map((c) =>
        editing !== 'new' && editing?.id === c.id ? null : (
          <div
            key={c.id}
            className="flex items-center justify-between border border-border rounded px-3 py-1.5 bg-white"
          >
            <span className={c.is_active ? '' : 'text-secondary-text line-through'}>{c.name}</span>
            <div className="flex items-center gap-1">
              <IconBtn
                icon={faPen}
                onClick={() => {
                  setEditing(c);
                  setName(c.name);
                }}
              />
              <IconBtn icon={c.is_active ? faEyeSlash : faEye} onClick={() => toggle(c)} />
            </div>
          </div>
        ),
      )}

      {editing ? (
        <div className="border border-primary rounded p-3 bg-blue-bg flex items-center gap-2">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onPressEnter={submit}
            maxLength={80}
            placeholder="שם הקטגוריה"
            autoFocus
          />
          <Button onClick={() => setEditing(null)}>ביטול</Button>
          <Button type="primary" onClick={submit} loading={save.isPending}>
            שמור
          </Button>
        </div>
      ) : (
        <Button
          icon={<FontAwesomeIcon icon={faPlus} />}
          onClick={() => {
            setEditing('new');
            setName('');
          }}
          className="mt-1"
        >
          הוסף קטגוריה
        </Button>
      )}
    </div>
  );
}
